// The Rouser logo — concentric pulse rings emanating from a solid dot.
// Reads as a radar ping or heartbeat: a literal visualisation of "to rouse"
// (to send out a signal that wakes / stirs / alerts).
//
// Pure SVG so it scales sharp at any size. The optional `animated` mode
// pulses each ring outward in sequence — used on the splash and welcome
// page where it earns the visual cost; the favicon and brand wordmark
// use the static form.

type Props = {
  size?: number;
  animated?: boolean;
  /** Override the solid dot + ring colour (defaults to var(--color-accent)). */
  color?: string;
};

export function Logo({ size = 64, animated = false, color }: Props) {
  const accent = color ?? 'var(--color-accent)';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      aria-hidden
    >
      <style>{`
        @keyframes ds-logo-pulse {
          0%   { transform: scale(0.6); opacity: 0; }
          30%  { opacity: var(--ring-peak, 0.7); }
          100% { transform: scale(1.0); opacity: 0; }
        }
        .ds-logo__ring {
          transform-box: fill-box;
          transform-origin: center;
        }
        .ds-logo__ring--animated {
          animation: ds-logo-pulse 2.4s var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1)) infinite;
        }
        .ds-logo__ring--delay-1 { animation-delay: 0.6s; }
        .ds-logo__ring--delay-2 { animation-delay: 1.2s; }
      `}</style>
      <circle
        cx="32"
        cy="32"
        r="26"
        stroke={accent}
        strokeWidth="1.5"
        className={`ds-logo__ring${animated ? ' ds-logo__ring--animated ds-logo__ring--delay-2' : ''}`}
        style={animated ? { ['--ring-peak' as string]: '0.18' } : undefined}
        opacity={animated ? undefined : '0.12'}
      />
      <circle
        cx="32"
        cy="32"
        r="18"
        stroke={accent}
        strokeWidth="1.5"
        className={`ds-logo__ring${animated ? ' ds-logo__ring--animated ds-logo__ring--delay-1' : ''}`}
        style={animated ? { ['--ring-peak' as string]: '0.36' } : undefined}
        opacity={animated ? undefined : '0.32'}
      />
      <circle
        cx="32"
        cy="32"
        r="10"
        stroke={accent}
        strokeWidth="1.75"
        className={`ds-logo__ring${animated ? ' ds-logo__ring--animated' : ''}`}
        style={animated ? { ['--ring-peak' as string]: '0.7' } : undefined}
        opacity={animated ? undefined : '0.7'}
      />
      <circle cx="32" cy="32" r="5" fill={accent} />
    </svg>
  );
}
